import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useMemo } from "react";
import { SidebarGroup, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useConversationRunning } from "@/hooks/useConversationRunning";
import { useNavigationData } from "@/hooks/useNavigationData";
import { buildProjectHomeConversationsDeepLink } from "@/lib/entityDeepLinks";
import { ErrorMessage } from "@/components/ui/error-message";
import { useConversationStore } from "@/stores/useConversationStore";
import { useCurrentUserSharesStore } from "@/stores/useCurrentUserSharesStore";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import type { CNSConversation } from "@/models/cns";
import { getRailRecentMeta, sessionStatusDotClass } from "@/lib/railRecentMeta";
import { cn } from "@/lib/utils";
const COLLAPSED_ICON_LIMIT = 5;
const RAIL_RECENT_LIMIT = 5;
interface RecentChatsProps {
  className?: string;
  collapsedIconMode?: boolean;
}
function formatRecentDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 24) {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit"
    });
  }
  if (diffInHours < 168) {
    return date.toLocaleDateString([], {
      weekday: "short"
    });
  }
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric"
  });
}
function RecentChatIconRow(t0) {
  const $ = _c(24);
  const {
    conversation,
    isActive,
    isSharedConversation,
    onSelect
  } = t0;
  const isRunningLocally = useConversationRunning(conversation.id);
  let t1;
  if ($[0] !== conversation || $[1] !== isRunningLocally) {
    t1 = getRailRecentMeta(conversation, {
      isRunningLocally
    });
    $[0] = conversation;
    $[1] = isRunningLocally;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const meta = t1;
  const t2 = conversation.updatedAt || conversation.createdAt;
  let t3;
  if ($[3] !== t2) {
    t3 = formatRecentDate(t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const dateLabel = t3;
  let parts;
  if ($[5] !== conversation.title || $[6] !== dateLabel || $[7] !== isSharedConversation || $[8] !== meta.secondaryLineFull) {
    parts = [conversation.title];
    if (isSharedConversation) {
      parts.push("(Shared)");
    }
    if (meta.secondaryLineFull) {
      parts.push(meta.secondaryLineFull);
    }
    parts.push(dateLabel);
    $[5] = conversation.title;
    $[6] = dateLabel;
    $[7] = isSharedConversation;
    $[8] = meta.secondaryLineFull;
    $[9] = parts;
  } else {
    parts = $[9];
  }
  const tooltip = parts.join(" \xB7 ");
  let t4;
  if ($[10] !== conversation.id || $[11] !== conversation.projectId || $[12] !== onSelect) {
    t4 = () => onSelect(conversation.id, conversation.projectId);
    $[10] = conversation.id;
    $[11] = conversation.projectId;
    $[12] = onSelect;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MessageCircle className="size-4" />;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== meta.sessionStatus) {
    t6 = cn("absolute -right-0.5 -top-0.5 size-1.5 rounded-full ring-2 ring-sidebar", sessionStatusDotClass(meta.sessionStatus));
    $[15] = meta.sessionStatus;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <span className="relative">{t5}<span className={t6} aria-hidden={true} /></span>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== isActive || $[20] !== t4 || $[21] !== t7 || $[22] !== tooltip) {
    t8 = <SidebarMenuItem><SidebarMenuButton size="sm" isActive={isActive} onClick={t4} tooltip={tooltip} className="justify-center">{t7}</SidebarMenuButton></SidebarMenuItem>;
    $[19] = isActive;
    $[20] = t4;
    $[21] = t7;
    $[22] = tooltip;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function RecentChatSidebarRow(t0) {
  const $ = _c(41);
  const {
    conversation,
    isActive,
    isSharedConversation,
    onSelect
  } = t0;
  const isRunningLocally = useConversationRunning(conversation.id);
  let t1;
  if ($[0] !== conversation || $[1] !== isRunningLocally) {
    t1 = getRailRecentMeta(conversation, {
      isRunningLocally
    });
    $[0] = conversation;
    $[1] = isRunningLocally;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const meta = t1;
  const t2 = conversation.updatedAt || conversation.createdAt;
  let t3;
  if ($[3] !== t2) {
    t3 = formatRecentDate(t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const dateLabel = t3;
  let t4;
  if ($[5] !== conversation.attentionHint) {
    t4 = conversation.attentionHint?.trim();
    $[5] = conversation.attentionHint;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const showMetaInsteadOfDate = Boolean(t4);
  let parts;
  if ($[7] !== conversation.title || $[8] !== dateLabel || $[9] !== isSharedConversation || $[10] !== meta.secondaryLineFull) {
    parts = [conversation.title];
    if (isSharedConversation) {
      parts.push("(Shared)");
    }
    if (meta.secondaryLineFull) {
      parts.push(meta.secondaryLineFull);
    }
    parts.push(dateLabel);
    $[7] = conversation.title;
    $[8] = dateLabel;
    $[9] = isSharedConversation;
    $[10] = meta.secondaryLineFull;
    $[11] = parts;
  } else {
    parts = $[11];
  }
  const tooltip = parts.join(" \xB7 ");
  let t5;
  if ($[12] !== conversation.id || $[13] !== conversation.projectId || $[14] !== onSelect) {
    t5 = () => onSelect(conversation.id, conversation.projectId);
    $[12] = conversation.id;
    $[13] = conversation.projectId;
    $[14] = onSelect;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <MessageCircle className="mt-0.5 size-4 shrink-0" />;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== conversation.title) {
    t7 = <span className="min-w-0 flex-1 truncate font-medium">{conversation.title}</span>;
    $[17] = conversation.title;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== isSharedConversation) {
    t8 = isSharedConversation ? <Badge variant="shared" className="shrink-0 text-[10px] py-0 px-2 font-medium">Shared</Badge> : null;
    $[19] = isSharedConversation;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = <div className="flex min-w-0 items-center gap-1.5">{t7}{t8}</div>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== meta.secondaryLine || $[25] !== meta.secondaryLineFull) {
    t10 = meta.secondaryLine ? <p className="truncate text-xs text-muted-foreground" title={meta.secondaryLineFull ?? meta.secondaryLine}>{meta.secondaryLine}</p> : null;
    $[24] = meta.secondaryLine;
    $[25] = meta.secondaryLineFull;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <div className="min-w-0 flex-1">{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== dateLabel || $[31] !== showMetaInsteadOfDate) {
    t12 = !showMetaInsteadOfDate ? <span className="ml-1 shrink-0 text-xs text-muted-foreground">{dateLabel}</span> : null;
    $[30] = dateLabel;
    $[31] = showMetaInsteadOfDate;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t12) {
    t13 = <div className="flex min-w-0 flex-1 items-start gap-2.5 overflow-hidden">{t6}{t11}{t12}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== isActive || $[37] !== t13 || $[38] !== t5 || $[39] !== tooltip) {
    t14 = <SidebarMenuItem><SidebarMenuButton size="sm" isActive={isActive} onClick={t5} className="h-auto items-start py-2" tooltip={tooltip}>{t13}</SidebarMenuButton></SidebarMenuItem>;
    $[36] = isActive;
    $[37] = t13;
    $[38] = t5;
    $[39] = tooltip;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
}
export function NavRecentChats(t0) {
  const $ = _c(43);
  const {
    className,
    collapsedIconMode: t1
  } = t0;
  const collapsedIconMode = t1 === undefined ? false : t1;
  const conversations = useConversationStore(_temp);
  const loading = useConversationStore(_temp2);
  const error = useConversationStore(_temp3);
  const fetchRecentConversations = useConversationStore(_temp4);
  const currentWorkspaceId = useWorkspaceStore(_temp5);
  const isShared = useCurrentUserSharesStore(_temp6);
  const {
    conversationId: activeConversationId,
    projectId: routeProjectId
  } = useNavigationData();
  const router = useRouter();
  const {
    state
  } = useSidebar();
  let t2;
  if ($[0] !== fetchRecentConversations) {
    t2 = () => {
      fetchRecentConversations();
    };
    $[0] = fetchRecentConversations;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== currentWorkspaceId || $[3] !== fetchRecentConversations) {
    t3 = [fetchRecentConversations, currentWorkspaceId];
    $[2] = currentWorkspaceId;
    $[3] = fetchRecentConversations;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== routeProjectId) {
    t4 = routeProjectId ? buildProjectHomeConversationsDeepLink(routeProjectId) : "/dashboard";
    $[5] = routeProjectId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const seeAllHref = t4;
  const seeAllLabel = routeProjectId ? "See all chats\u2026" : "Browse projects\u2026";
  let t5;
  if ($[7] !== router) {
    t5 = (conversationId, projectId) => {
      if (projectId) {
        router.push(`/dashboard/projects/${projectId}/conversations/${conversationId}`);
        return;
      }
      toast.error("Couldn't open chat \u2014 missing project context.");
    };
    $[7] = router;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleChatSelect = t5;
  if (state === "collapsed" && collapsedIconMode) {
    let T0;
    let T1;
    let t6;
    let t7;
    let t8;
    if ($[9] !== activeConversationId || $[10] !== className || $[11] !== conversations || $[12] !== handleChatSelect || $[13] !== isShared || $[14] !== loading) {
      const iconConversations = conversations.slice(0, COLLAPSED_ICON_LIMIT);
      t8 = className;
      T1 = SidebarGroup;
      t7 = "py-0";
      T0 = SidebarMenu;
      t6 = loading ? Array.from({
        length: 3
      }).map(_temp7) : iconConversations.map(conversation => <RecentChatIconRow key={conversation.id} conversation={conversation} isActive={conversation.id === activeConversationId} isSharedConversation={isShared("conversation", conversation.id)} onSelect={handleChatSelect} />);
      $[9] = activeConversationId;
      $[10] = className;
      $[11] = conversations;
      $[12] = handleChatSelect;
      $[13] = isShared;
      $[14] = loading;
      $[15] = T0;
      $[16] = T1;
      $[17] = t6;
      $[18] = t7;
      $[19] = t8;
    } else {
      T0 = $[15];
      T1 = $[16];
      t6 = $[17];
      t7 = $[18];
      t8 = $[19];
    }
    let t9;
    if ($[20] !== T0 || $[21] !== t6) {
      t9 = <T0>{t6}</T0>;
      $[20] = T0;
      $[21] = t6;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== T1 || $[24] !== t7 || $[25] !== t9) {
      t10 = <T1 className={t7}>{t9}</T1>;
      $[23] = T1;
      $[24] = t7;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== t10 || $[28] !== t8) {
      t11 = <div className={t8}>{t10}</div>;
      $[27] = t10;
      $[28] = t8;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    return t11;
  }
  if (state === "collapsed") {
    return null;
  }
  let t6;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SidebarGroupLabel>Recent</SidebarGroupLabel>;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== activeConversationId || $[32] !== conversations || $[33] !== error || $[34] !== handleChatSelect || $[35] !== isShared || $[36] !== loading || $[37] !== seeAllHref || $[38] !== seeAllLabel) {
    t7 = <SidebarGroup className="py-0">{t6}<SidebarMenu>{loading ? <div className="px-3 py-2"><div className="h-4 bg-muted rounded animate-pulse mb-2" /><div className="h-4 bg-muted rounded animate-pulse mb-2" /><div className="h-4 bg-muted rounded animate-pulse" /></div> : error ? <div className="px-3 py-2"><ErrorMessage message={error} /></div> : conversations.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No recent chats</div> : <>{conversations.slice(0, RAIL_RECENT_LIMIT).map(conversation_0 => <RecentChatSidebarRow key={conversation_0.id} conversation={conversation_0} isActive={conversation_0.id === activeConversationId} isSharedConversation={isShared("conversation", conversation_0.id)} onSelect={handleChatSelect} />)}{conversations.length > RAIL_RECENT_LIMIT ? <SidebarMenuItem><SidebarMenuButton asChild={true} size="sm" className="text-muted-foreground"><Link href={seeAllHref}>{seeAllLabel}</Link></SidebarMenuButton></SidebarMenuItem> : null}</>}</SidebarMenu></SidebarGroup>;
    $[31] = activeConversationId;
    $[32] = conversations;
    $[33] = error;
    $[34] = handleChatSelect;
    $[35] = isShared;
    $[36] = loading;
    $[37] = seeAllHref;
    $[38] = seeAllLabel;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  let t8;
  if ($[40] !== className || $[41] !== t7) {
    t8 = <div className={className}>{t7}</div>;
    $[40] = className;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  return t8;
}
function _temp7(_, i) {
  return <SidebarMenuItem key={`recent-skeleton-${i}`}><SidebarMenuButton disabled={true} className="justify-center"><div className="size-4 rounded bg-muted animate-pulse" /></SidebarMenuButton></SidebarMenuItem>;
}
function _temp6(s_4) {
  return s_4.isShared;
}
function _temp5(s_3) {
  return s_3.currentWorkspaceId;
}
function _temp4(s_2) {
  return s_2.fetchRecentConversations;
}
function _temp3(s_1) {
  return s_1.recentError;
}
function _temp2(s_0) {
  return s_0.recentLoading;
}
function _temp(s) {
  return s.recentConversations;
}